// assets/section-scrolling-logos.js

class ScrollingLogos extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.setupPrefersReducedMotion();
    this.setupImageLazyLoading();
    this.pauseOnHover();
  }

  setupIntersectionObserver() {
    // Pause animations when section is not visible to improve performance
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wrappers = entry.target.querySelectorAll(
            ".scrolling-logos__wrapper"
          );
          wrappers.forEach((wrapper) => {
            if (entry.isIntersecting) {
              wrapper.style.animationPlayState = "running";
            } else {
              wrapper.style.animationPlayState = "paused";
            }
          });
        });
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    observer.observe(this);
  }

  setupPrefersReducedMotion() {
    // Handle reduced motion preference changes
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const handleReducedMotion = (e) => {
      const tracks = this.querySelectorAll(".scrolling-logos__track");
      tracks.forEach((track) => {
        if (e.matches) {
          track.classList.add("reduced-motion");
          this.makeScrollable(track);
        } else {
          track.classList.remove("reduced-motion");
          this.removeScrollable(track);
        }
      });
    };

    mediaQuery.addListener(handleReducedMotion);
    handleReducedMotion(mediaQuery);
  }

  makeScrollable(track) {
    // Convert animated track to scrollable version
    track.style.overflowX = "auto";
    track.style.scrollbarWidth = "none";
    track.style.msOverflowStyle = "none";

    const wrapper = track.querySelector(".scrolling-logos__wrapper");
    const duplicates = track.querySelectorAll(
      ".scrolling-logos__list--duplicate"
    );

    if (wrapper) {
      wrapper.style.animation = "none";
    }

    duplicates.forEach((duplicate) => {
      duplicate.style.display = "none"; // Hide duplicates
    });
  }

  removeScrollable(track) {
    // Restore animated version
    track.style.overflowX = "hidden";

    const wrapper = track.querySelector(".scrolling-logos__wrapper");
    const duplicates = track.querySelectorAll(
      ".scrolling-logos__list--duplicate"
    );

    if (wrapper) {
      wrapper.style.animation = "";
    }

    duplicates.forEach((duplicate) => {
      duplicate.style.display = "flex"; // Show duplicates
    });
  }

  setupImageLazyLoading() {
    // Enhanced lazy loading for better performance
    const images = this.querySelectorAll(".scrolling-logos__image");

    if ("IntersectionObserver" in window) {
      const imageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target;

              // Preload higher quality image
              const highQualityUrl = img.src.replace(
                /width=\d+/,
                "width=" + parseInt(img.width) * 2
              );
              const preloadImg = new Image();
              preloadImg.onload = () => {
                img.src = highQualityUrl;
                img.classList.add("loaded");
              };
              preloadImg.src = highQualityUrl;

              imageObserver.unobserve(img);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "100px",
        }
      );

      images.forEach((img) => {
        imageObserver.observe(img);
      });
    }
  }

  pauseOnHover() {
    // Pause animation on hover for better UX
    const tracks = this.querySelectorAll(".scrolling-logos__track");

    tracks.forEach((track) => {
      track.addEventListener("mouseenter", () => {
        if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          const wrapper = track.querySelector(".scrolling-logos__wrapper");
          if (wrapper) {
            wrapper.style.animationPlayState = "paused";
          }
        }
      });

      track.addEventListener("mouseleave", () => {
        if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          const wrapper = track.querySelector(".scrolling-logos__wrapper");
          if (wrapper) {
            wrapper.style.animationPlayState = "running";
          }
        }
      });
    });
  }

  // Performance monitoring
  monitorPerformance() {
    if ("PerformanceObserver" in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.name.includes("scrolling-logos") && entry.duration > 16) {
            console.warn("Scrolling logos performance issue:", entry);
          }
        });
      });

      observer.observe({ entryTypes: ["measure"] });
    }
  }
}

// Register custom element
customElements.define("scrolling-logos", ScrollingLogos);

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  const scrollingLogosSections = document.querySelectorAll(
    ".scrolling-logos-section"
  );

  scrollingLogosSections.forEach((section) => {
    // Wrap in custom element if not already
    if (section.tagName.toLowerCase() !== "scrolling-logos") {
      const wrapper = document.createElement("scrolling-logos");
      section.parentNode.insertBefore(wrapper, section);
      wrapper.appendChild(section);
    }
  });
});

// Handle resize events for responsive adjustments
let resizeTimeout;
window.addEventListener("resize", () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    // Force recalculation of animations on resize
    const scrollingLogos = document.querySelectorAll("scrolling-logos");
    scrollingLogos.forEach((element) => {
      const wrappers = element.querySelectorAll(".scrolling-logos__wrapper");
      wrappers.forEach((wrapper) => {
        // Trigger animation restart
        wrapper.style.animation = "none";
        wrapper.offsetHeight; // Trigger reflow
        wrapper.style.animation = "";
      });
    });
  }, 250);
});
